export const DEFAULT_PAGE_SIZE = 20
export const AUTHORIZATION_HEADER_PREFIX = 'SessionToken '

export const YES_NO_FLAG = {
  YES: 'Y',
  NO: 'N',
} as const

export type YesNoFlag = (typeof YES_NO_FLAG)[keyof typeof YES_NO_FLAG]

export const CLIENT_PLATFORM_HEADER = 'X-Client-Platform'

// Which client minted a session. Single-session enforcement is scoped by this
// (AUTH-22): a `mobile` login revokes the user's prior `mobile` sessions and
// detaches their push tokens.
//
// PET-2999 adds `supervisor` because the supervisor app is a SECOND mobile app.
// Were it to send `mobile` too, a person holding both would be signed out of one
// every time they opened the other, and would lose push on both — the revocation
// is per person and the push-token detach is not platform-scoped at all. The
// value exists here, and its CHECK constraint ships with this migration, so the
// app side (PET-3003) does not need a second one.
export const SESSION_CLIENT_PLATFORMS = ['web', 'mobile', 'supervisor'] as const

export type SessionClientPlatform = (typeof SESSION_CLIENT_PLATFORMS)[number]

/**
 * Platforms that revoke their own prior sessions on login — "one phone per
 * person, per app".
 *
 * A property of the platform list rather than a boolean OR at the call site, so
 * a third mobile client is one entry here instead of an edit inside
 * `createSession`. `web` is absent: AUTH-03 allows concurrent browser sessions.
 */
export const SINGLE_SESSION_PLATFORMS: readonly SessionClientPlatform[] = ['mobile', 'supervisor']

/**
 * What a NULL `client_platform` counts as when revoking. Those rows predate the
 * column and every one of them is a driver-app session, so reading them as
 * `web` would let a legacy phone session outlive a new login (AUTH-22).
 */
export const LEGACY_NULL_CLIENT_PLATFORM: SessionClientPlatform = 'mobile'

// Which channel a sign-in one-time code is delivered on (PET-3803). Stored on
// `core.users.otp_channel` as the person's default rather than per client:
// a driver who picks "text" on their phone means it, and would not expect the
// browser to keep mailing them (see AUTH-22 for what *is* per-client).
export const OTP_CHANNELS = ['email', 'sms'] as const

export type OtpChannel = (typeof OTP_CHANNELS)[number]
